import { PERMISSIONS } from '../constants/permissions';
import type { ProjectModuleCapabilities, ProjectScope } from '../types/project';
import { hasRole, ROLE_HR_ADMIN, ROLE_TEAM_LEAD } from '../utils/approvalRoles';
import { hasPermission } from '../utils/permissions';

/**
 * Project module capabilities from mounted arkhrmsapi project.routes.js.
 *
 * Mounted:
 * - GET /projects
 * - POST /projects
 * - GET /projects/:projectId
 * - GET /projects/:projectId/board
 * - GET /projects/:projectId/completed-tasks
 * - GET /projects/:projectId/tasks/:taskId
 * - POST /projects/:projectId/tasks
 * - PUT /projects/:projectId/tasks/:taskId
 * - PUT /projects/:projectId/tasks/:taskId/move
 * - POST /projects/:projectId/tasks/:taskId/subtasks
 * - PUT /projects/:projectId/tasks/:taskId/subtasks/reorder
 * - PUT /projects/:projectId/subtasks/:subtaskId
 * - DELETE /projects/:projectId/tasks/:taskId
 * - DELETE /projects/:projectId/subtasks/:subtaskId
 * - POST /projects/:projectId/members/bulk (Edit Project → add members)
 * - remaining member/column write routes (reserved for later edit UI)
 * - GET /projects/reports/employees
 * - GET /projects/reports/tasks (format JSON | CSV | XLSX)
 *
 * Installed permissions: PROJECT_VIEW, PROJECT_CREATE, PROJECT_UPDATE.
 */
export const PROJECT_ROUTE_CAPABILITIES = {
  list: true,
  create: true,
  details: true,
  update: true,
  board: true,
  createTask: true,
  moveTask: true,
  taskReport: true,
} as const;

const ORGANIZATION_WIDE_ROLES = ['SUPER_ADMIN', ROLE_HR_ADMIN] as const;

/**
 * Mirrors the API visibility rules (the API enforces them; this only shapes
 * the UI): HR Admin → all projects, Team Lead → projects they created,
 * everyone else → projects assigned to them.
 */
export function getProjectScope(roles: readonly string[] | undefined): ProjectScope {
  if (ORGANIZATION_WIDE_ROLES.some(role => hasRole(roles, role))) return 'ALL';
  if (hasRole(roles, ROLE_TEAM_LEAD)) return 'CREATOR';
  return 'MEMBER';
}

export function getProjectModuleCapabilities(
  permissions: readonly string[],
  roles?: readonly string[],
): ProjectModuleCapabilities {
  const scope = getProjectScope(roles);
  const canView = hasPermission(permissions, PERMISSIONS.PROJECT_VIEW);
  const canCreate = hasPermission(permissions, PERMISSIONS.PROJECT_CREATE);
  const canUpdate = hasPermission(permissions, PERMISSIONS.PROJECT_UPDATE);
  return {
    scope,
    canViewProjects: canView && PROJECT_ROUTE_CAPABILITIES.list,
    canCreateProject:
      canCreate && scope !== 'MEMBER' && PROJECT_ROUTE_CAPABILITIES.create,
    canUpdateProject: canUpdate && PROJECT_ROUTE_CAPABILITIES.update,
    // The API resolves which projects this applies to (HR Admin → all, Team
    // Lead → projects they created); employees never manage members.
    canEditProjectMembers:
      canUpdate && scope !== 'MEMBER' && PROJECT_ROUTE_CAPABILITIES.update,
    // Employees work on tasks (add / move / log time) in the projects assigned
    // to them with PROJECT_VIEW alone; the API limits them to those projects.
    canManageTasks:
      (canUpdate || (canView && scope === 'MEMBER')) &&
      PROJECT_ROUTE_CAPABILITIES.createTask,
    // Every role with PROJECT_VIEW; the API limits rows to the role's scope.
    canViewTaskReport: canView && PROJECT_ROUTE_CAPABILITIES.taskReport,
  };
}
